import { cloneElement, isValidElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CompanyLogo, PrismLogo } from '../../assets/icons';

export type AppTileData = {
  id: string;
  logo?: ReactNode | string;
  title: string;
  description: string;
  link?: string;
  external?: boolean;
  disabled?: boolean;
};

export type AppTileCardProps = Omit<AppTileData, 'id'>;

const appTiles: AppTileData[] = [
  {
    id: 'prism',
    title: 'PRISM',
    logo: <PrismLogo />,
    description:
      'Designed for developers and forward thinking CRE professionals. Prism offers a comprehensive suite of tools to streamline operations, enhance tenant experience, and maximize asset value.',
    link: '/prism/reference-docs',
  },
  // {
  //   id: 'hank',
  //   title: 'HANK',
  //   description:
  //     "Hank's AI-powered HVAC optimization solution provides a new, innovative way to reduce energy consumption and operating expenses.",
  //   disabled: true,
  // },
  // {
  //   id: 'api',
  //   title: 'API Hub',
  //   logo: <CompanyLogo />,

  //   description:
  //     'It is a JLL API where you can explore various features and functionalities.',
  //   link: 'https://api-developer.jll.com',
  //   external: true,
  // },
  // {
  //   id: 'apisandbox',
  //   title: 'GPT Developer Sandbox',
  //   logo: <CompanyLogo />,

  //   description:
  //     "The JLL GPT API provides a simple interface for developers to create an intelligence layer in their applications, powered by OpenAI's state of the art models.",
  //   link: 'https://ai.jll.com/developer-sandbox',
  //   external: true,
  // },
];

const LogoPlaceholder: React.FC = () => (
  <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-bold">
    <span>!</span>
  </div>
);

function renderLogo(logo: React.ReactNode | string | undefined, title: string) {
  if (!logo) return <LogoPlaceholder />;
  if (typeof logo === 'string') {
    return <img src={logo} alt={title} className="w-8 h-8 object-contain" />;
  }
  if (isValidElement(logo)) {
    if (logo.type === PrismLogo || logo.type === CompanyLogo) {
      return (
        <div className="w-10 h-10 flex items-center justify-center">
          {cloneElement(logo, { ...logo.props, className: 'w-8 h-8' })}
        </div>
      );
    }
    return (
      <div className="w-10 h-10 flex items-center justify-center">{logo}</div>
    );
  }
  return logo;
}

export const AppTileCard: React.FC<AppTileCardProps> = ({
  logo,
  title,
  description,
  link = '#',
  external = false,
  disabled = false,
}) => {
  const cardClasses = `bg-gray-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-lg transition-all duration-300 w-80 ${
    disabled
      ? 'opacity-50 cursor-not-allowed'
      : 'hover:scale-105 hover:shadow-2xl'
  }`;

  const linkProps = external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  const CardContent = (
    <>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {renderLogo(logo, title)}
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
          <div className="text-green-400">
            <ArrowRight className="h-5 w-5" />
          </div>
        </div>
        <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
      </div>
    </>
  );

  if (disabled) {
    return <div className={cardClasses}>{CardContent}</div>;
  }

  if (external) {
    return (
      <a href={link} {...linkProps} className={cardClasses}>
        {CardContent}
      </a>
    );
  }

  return (
    <Link to={link} className={cardClasses}>
      {CardContent}
    </Link>
  );
};

const Dashboard: React.FC = () => {
  return (
    <div className="h-screen bg-gray-100">
      <div className="text-center py-10 px-4">
        <h2 className="text-4xl font-extrabold text-gray-800 mb-4">
          Welcome to the Property Management Technology Developer Portal!
        </h2>
        <p className="text-gray-600 text-lg mx-auto">
          Explore our powerful APIs, check out our technical docs, and build
          innovative integrations with ease.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center gap-6 pb-10">
        {appTiles.map(({ id, ...tile }) => (
          <AppTileCard key={id} {...tile} />
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
